<template>
	<!-- a paged Operators audit feed for the selected client: the whole client's log, or one
	     operator's history when targetUserId is set. Loads its first page when mounted -->
	<div class="audit-feed" data-cy="auditFeed">
		<div v-if="loaded && !entries.length" class="hint" data-cy="auditEmpty">
			{{ filtered ? label('operatorAuditNoMatches', 'No entries match the filters') : $t('text.noEntries') }}
		</div>
		<OperatorAuditEntry v-for="entry of entries" :key="entry.id"
			:entry="entry"
			:target-name="targetName(entry)"
			:hide-target="hideTarget"
			:dimension-values="dimensionValues"
			:peak-roles="peakRoles"
			:expanded="expandedId === entry.id"
			v-model:diffOnly="diffOnly"
			@toggle="expandedId = expandedId === entry.id ? null : entry.id"
		/>
		<TablePaginator v-model="skip" :limit="limit" :total="total" :results="entries" @update:modelValue="load()" />
	</div>
</template>

<script>
import TablePaginator from '@/components/common/TablePaginator.vue'
import OperatorAuditEntry from './OperatorAuditEntry.vue'
import { auditFilterRequest, hasAuditFilters } from './operator-audit-filters'
import { translatedLabel } from './operator-audit-lines'
import OperatorsExecutive from '../../../../../api2/src/modules/operators/OperatorsExecutive'

export default {
	name: 'OperatorAuditList',
	components: { TablePaginator, OperatorAuditEntry },
	props: {
		// one operator's history; omitted = every operator of the client
		targetUserId: String,
		hideTarget: Boolean,
		// the loaded operator list, used to name targets
		operators: { type: Array, default: () => [] },
		limit: { type: Number, default: 15 },
		// Report Center dimension values the host already loads, to name scope ids
		dimensionValues: { type: Object, default: () => ({}) },
		// Peak roles the host already loads, to name the stored Peaksolution role id
		peakRoles: { type: Array, default: () => [] },
		// the global log's filter bar state (OperatorAuditFilters); a change reloads from page one
		filters: { type: Object, default: () => ({}) },
	},
	// loading: true/false around every page load (the host decides how to show it);
	// error: the raw load error (the host handles 401 → login and error display);
	// actors: every acting e-mail in the client's log, for the admin filter
	emits: [ 'loading', 'error', 'actors' ],
	data() { return {
		entries: [],
		total: 0,
		skip: 0,
		loaded: false,
		expandedId: null,
		diffOnly: true,
	}},
	computed: {
		filtered() {
			return hasAuditFilters(this.filters)
		},
	},
	watch: {
		targetUserId() { this.reload() },
		filters: { handler() { this.reload() }, deep: true },
	},
	mounted() {
		this.load()
	},
	methods: {
		// back to the first page, everything collapsed
		async reload() {
			this.skip = 0
			this.expandedId = null
			await this.load()
		},
		async load() {
			this.$emit('loading', true)
			try {
				const executive = new OperatorsExecutive(this)
				const request = { ...auditFilterRequest(this.filters), skip: this.skip, limit: this.limit }
				if (this.targetUserId) request.userId = this.targetUserId
				const result = await executive.getClientAuditLog(this.$store.state.selectedClient.sys.id, request)
				this.entries = result.entries
				this.total = result.total
				this.loaded = true
				this.$emit('actors', result.actors ?? [])
			}
			catch (error) {
				this.$emit('error', error)
			}
			this.$emit('loading', false)
		},
		// target name from the loaded operator list; deleted operators fall back to the
		// e-mail recorded in the entry itself, then to a sibling entry of the same operator
		// on this page (their create/delete rows carry it); the bare id is the last resort
		targetName(entry) {
			const operator = this.operators.find(op => op.id === entry.entityId)
			if (operator?.email) return operator.email
			const own = entry.newData?.email ?? entry.oldData?.email
			if (own) return own
			const sibling = this.entries.find(e => e.entityId === entry.entityId && (e.newData?.email || e.oldData?.email))
			return sibling?.newData?.email ?? sibling?.oldData?.email ?? entry.entityId
		},
		label(key, fallback) {
			return translatedLabel(this, key, fallback)
		},
	},
}
</script>

<style scoped>
.audit-feed { padding: 10px; width: 100%; }
.hint { font-size: 11px; color: #888; padding-bottom: 4px; }
</style>
